import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AudioTrack } from '../../hooks/useWebAudio';
import { SynchronizedWaveformCanvas } from './SynchronizedWaveformCanvas';
import { WaveformContextMenu } from './WaveformContextMenu';
import { BrainwaveTrackLane } from './BrainwaveTrackLane';
import { Icon } from '../common/Icon';
import { FaVolumeUp, FaVolumeMute, FaTimes } from 'react-icons/fa';

const LaneContainer = styled.div<{ $trackColor: string; $isDragging?: boolean }>`
  display: flex;
  background: #222;
  border-bottom: 1px solid #333;
  min-height: 80px;
  border-left: 4px solid ${props => props.$trackColor};
  opacity: ${props => props.$isDragging ? 0.8 : 1};
  transition: opacity 0.2s ease, transform 0.2s ease;
  box-shadow: ${props => props.$isDragging ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'};
`;

const TrackHeader = styled.div`
  width: 182px;
  min-width: 182px;
  background: #2a2a2a;
  border-right: 1px solid #333;
  display: flex;
  flex-direction: column;
  padding: 8px;
  position: relative;
`;

const TrackInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const TrackName = styled.div`
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrackType = styled.div`
  color: #888;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TrackControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const ControlButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? '#4caf50' : '#333'};
  color: white;
  border: none;
  padding: 4px 6px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 3px;

  &:hover {
    background: ${props => props.$active ? '#45a049' : '#444'};
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const VolumeSlider = styled.input`
  width: 80px;
  height: 4px;
  accent-color: #4caf50;
  background: #444;
  border-radius: 2px;
`;

const VolumeLabel = styled.span`
  color: #888;
  font-size: 10px;
  min-width: 25px;
  text-align: right;
`;

const TrackContent = styled.div<{ $isDragging?: boolean }>`
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #1a1a1a;
  
  &:hover {
    background: #222;
  }
`;

const ResizeHandle = styled.div<{ $isResizing?: boolean }>`
  position: absolute;
  top: 0;
  right: -4px;
  bottom: 0;
  width: 8px;
  background: ${props => props.$isResizing ? '#4caf50' : 'transparent'};
  border-right: 2px solid ${props => props.$isResizing ? '#4caf50' : '#888'};
  cursor: ew-resize;
  opacity: ${props => props.$isResizing ? 1 : 0};
  transition: opacity 0.2s;
  z-index: 10;

  &:hover {
    opacity: 1;
    background: rgba(76, 175, 80, 0.2);
  }
`;

const FadeHandle = styled.div<{ $isActive?: boolean; $position: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  ${props => props.$position === 'left' ? 'left: 0' : 'right: 0'};
  width: 20px;
  height: 20px;
  cursor: ew-resize;
  opacity: ${props => props.$isActive ? 1 : 0};
  transition: opacity 0.2s;
  z-index: 10;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    ${props => props.$position === 'left' ? 'left: 2px' : 'right: 2px'};
    width: 0;
    height: 0;
    border-style: solid;
    border-width: ${props => props.$position === 'left' ? '8px 8px 8px 0' : '8px 0 8px 8px'};
    border-color: ${props => props.$position === 'left' 
      ? `transparent ${props.$isActive ? '#4caf50' : '#fff'} transparent transparent`
      : `transparent transparent transparent ${props.$isActive ? '#4caf50' : '#fff'}`
    };
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  }

  &:hover::after {
    border-color: ${props => props.$position === 'left' 
      ? 'transparent #4caf50 transparent transparent'
      : 'transparent transparent transparent #4caf50'
    };
  }
`;

const WaveformContainer = styled.div<{ $startTime: number; $pixelsPerSecond: number; $duration: number; $isDragging?: boolean; $color: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${props => props.$startTime * props.$pixelsPerSecond}px;
  width: ${props => props.$duration * props.$pixelsPerSecond}px;
  overflow: hidden;
  border-radius: 4px;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  border: 2px solid ${props => props.$color};
  background: ${props => props.$color}10;
  
  &:hover {
    border-color: ${props => props.$isDragging ? '#4caf50' : props.$color};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &:hover ${ResizeHandle} {
    opacity: 1;
  }

  &:hover ${FadeHandle} {
    opacity: 1;
  }
`;

const EmptyTrackMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;
  padding-left: 10px;
  height: 100%;
  color: #666;
  font-size: 12px;
  font-style: italic;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  color: #f44336;
  background-color: transparent;
  border: none;
  width: 18px;
  height: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.2s;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    color: #d32f2f;
    transform: scale(1.1);
  }
`;


const DurationDisplay = styled.span<{ $isTrimmed?: boolean }>`
  color: ${props => props.$isTrimmed ? '#ffa726' : '#888'};
  font-size: 10px;
  font-family: monospace;
  margin-left: auto;
`;

const FadeOverlay = styled.div<{ $type: 'in' | 'out'; $width: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${props => props.$type === 'in' ? 'left: 0' : 'right: 0'};
  width: ${props => props.$width}px;
  background: ${props => props.$type === 'in' 
    ? 'linear-gradient(to right, rgba(76, 175, 80, 0.3), transparent)'
    : 'linear-gradient(to left, rgba(76, 175, 80, 0.3), transparent)'
  };
  pointer-events: none;
`;

const FadeDurationLabel = styled.div`
  position: absolute;
  top: 22px;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.8);
  color: #4caf50;
  font-size: 11px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 11;
`;


interface TrackLaneProps {
  track: AudioTrack;
  pixelsPerSecond: number;
  visibleStartTime?: number;
  visibleEndTime?: number;
  onUpdateTrack: (trackId: string, updates: Partial<AudioTrack>) => void;
  onRemoveTrack?: (trackId: string) => void;
  onDuplicateTrack?: (trackId: string) => void;
  onLoopTrack?: (trackId: string) => void;
  viewportRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  // Drag handlers
  onDragStart?: (trackId: string, startTime: number, currentPosition: number) => void;
  onDragUpdate?: (currentTime: number) => number;
  onDragEnd?: () => void;
  isDragging?: boolean;
  dragTrackId?: string | null;
  // Timeline playback state for brainwave tracks
  isPlaying?: boolean;
  currentTime?: number;
}

export const TrackLane: React.FC<TrackLaneProps> = ({
  track,
  pixelsPerSecond,
  visibleStartTime = 0,
  visibleEndTime = 300,
  onUpdateTrack,
  onRemoveTrack,
  onDuplicateTrack,
  onLoopTrack,
  viewportRef,
  className,
  onDragStart,
  onDragUpdate,
  onDragEnd,
  isDragging,
  dragTrackId,
  isPlaying,
  currentTime
}) => {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isFadingIn, setIsFadingIn] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [fadeInDuration, setFadeInDuration] = useState(track.fadeIn || 0);
  const [fadeOutDuration, setFadeOutDuration] = useState(track.fadeOut || 0);
  
  // Update fade durations when track properties change
  useEffect(() => {
    setFadeInDuration(track.fadeIn || 0);
  }, [track.fadeIn]);
  
  useEffect(() => {
    setFadeOutDuration(track.fadeOut || 0);
  }, [track.fadeOut]);
  
  const resizeStartRef = useRef<{ x: number; originalDuration: number } | null>(null);
  const fadeInStartRef = useRef<{ x: number; originalFade: number } | null>(null);
  const fadeOutStartRef = useRef<{ x: number; originalFade: number } | null>(null);
  
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    onUpdateTrack(track.id, { volume });
  }, [track.id, onUpdateTrack]);

  const handleBrainwaveSettingsUpdate = useCallback((trackId: string, settings: any) => {
    onUpdateTrack(trackId, { brainwaveSettings: settings });
  }, [onUpdateTrack]);

  const handleMuteToggle = useCallback(() => {
    onUpdateTrack(track.id, { muted: !track.muted });
  }, [track.id, track.muted, onUpdateTrack]);

  const handleRemove = useCallback(() => {
    if (onRemoveTrack) {
      onRemoveTrack(track.id);
    }
  }, [track.id, onRemoveTrack]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleDuplicate = useCallback(() => {
    if (onDuplicateTrack) {
      onDuplicateTrack(track.id);
    }
    handleCloseContextMenu();
  }, [track.id, onDuplicateTrack, handleCloseContextMenu]);

  const handleLoop = useCallback(() => {
    if (onLoopTrack) {
      onLoopTrack(track.id);
    }
    handleCloseContextMenu();
  }, [track.id, onLoopTrack, handleCloseContextMenu]);

  // Resize functionality
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!viewportRef?.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      originalDuration: track.trimmedDuration || track.duration
    };
  }, [track.duration, track.trimmedDuration]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeStartRef.current || !viewportRef?.current) return;
    
    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaDuration = deltaX / pixelsPerSecond;
    const newDuration = Math.max(0.5, resizeStartRef.current.originalDuration + deltaDuration);
    
    // For brainwave and noise tracks, allow unlimited expansion; for audio tracks, clamp to original duration
    const finalDuration = (track.type === 'brainwave' || track.type === 'noise') ? newDuration : Math.min(newDuration, track.duration);
    
    onUpdateTrack(track.id, { trimmedDuration: finalDuration });
  }, [isResizing, pixelsPerSecond, track.id, track.duration, track.type, onUpdateTrack]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    resizeStartRef.current = null;
  }, []);

  // Fade In functionality
  const handleFadeInStart = useCallback((e: React.MouseEvent) => {
    if (!viewportRef?.current || track.type === 'brainwave') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsFadingIn(true);
    fadeInStartRef.current = {
      x: e.clientX,
      originalFade: track.fadeIn || 0
    };
  }, [track.fadeIn]);

  const handleFadeInMove = useCallback((e: MouseEvent) => {
    if (!isFadingIn || !fadeInStartRef.current || !viewportRef?.current || track.type === 'brainwave') return;
    
    const deltaX = e.clientX - fadeInStartRef.current.x;
    const deltaDuration = deltaX / pixelsPerSecond;
    const newFadeIn = Math.max(0, fadeInStartRef.current.originalFade + deltaDuration);
    const maxFade = (track.trimmedDuration || track.duration) * 0.5; // Max 50% of track
    const clampedFadeIn = Math.min(newFadeIn, maxFade);
    
    setFadeInDuration(clampedFadeIn);
    onUpdateTrack(track.id, { fadeIn: clampedFadeIn });
  }, [isFadingIn, pixelsPerSecond, track.id, track.duration, track.trimmedDuration, onUpdateTrack]);

  const handleFadeInEnd = useCallback(() => {
    setIsFadingIn(false);
    fadeInStartRef.current = null;
  }, []);

  // Fade Out functionality
  const handleFadeOutStart = useCallback((e: React.MouseEvent) => {
    if (!viewportRef?.current || track.type === 'brainwave') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsFadingOut(true);
    fadeOutStartRef.current = {
      x: e.clientX,
      originalFade: track.fadeOut || 0
    };
  }, [track.fadeOut]);

  const handleFadeOutMove = useCallback((e: MouseEvent) => {
    if (!isFadingOut || !fadeOutStartRef.current || !viewportRef?.current || track.type === 'brainwave') return;
    
    const deltaX = fadeOutStartRef.current.x - e.clientX; // Reversed for right-side drag
    const deltaDuration = deltaX / pixelsPerSecond;
    const newFadeOut = Math.max(0, fadeOutStartRef.current.originalFade + deltaDuration);
    const maxFade = (track.trimmedDuration || track.duration) * 0.5; // Max 50% of track
    const clampedFadeOut = Math.min(newFadeOut, maxFade);
    
    setFadeOutDuration(clampedFadeOut);
    onUpdateTrack(track.id, { fadeOut: clampedFadeOut });
  }, [isFadingOut, pixelsPerSecond, track.id, track.duration, track.trimmedDuration, onUpdateTrack]);

  const handleFadeOutEnd = useCallback(() => {
    setIsFadingOut(false);
    fadeOutStartRef.current = null;
  }, []);

  // Drag functionality
  const dragStartRef = useRef<{ x: number; time: number } | null>(null);
  const isCurrentlyDragging = isDragging && dragTrackId === track.id;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!onDragStart || !viewportRef?.current) return;
    if (e.button !== 0) return; // only left click drag
    // Don't start drag if clicking on controls
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
    
    // Prevent default to avoid text selection and other browser behaviors
    e.preventDefault();
    e.stopPropagation();
    
    const rect = viewportRef.current.getBoundingClientRect();
    const timelineHeaderWidth = 201; // Account for track header
    const x = e.clientX - rect.left - timelineHeaderWidth;
    const time = x / pixelsPerSecond;
    
    dragStartRef.current = { x, time };
    onDragStart(track.id, time, track.startTime || 0);
  }, [onDragStart, viewportRef, pixelsPerSecond, track.id, track.startTime]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isCurrentlyDragging || !onDragUpdate || !viewportRef?.current || !dragStartRef.current) return;
    
    const rect = viewportRef.current.getBoundingClientRect();
    const timelineHeaderWidth = 201;
    const x = e.clientX - rect.left - timelineHeaderWidth;
    const time = x / pixelsPerSecond;
    
    const newStartTime = onDragUpdate(time);
    
    // Update track position during drag
    if (newStartTime !== (track.startTime || 0)) {
      onUpdateTrack(track.id, { startTime: newStartTime });
    }
  }, [isCurrentlyDragging, onDragUpdate, viewportRef, pixelsPerSecond, track.id, track.startTime, onUpdateTrack]);

  const handleMouseUp = useCallback(() => {
    if (isCurrentlyDragging && onDragEnd) {
      onDragEnd();
    }
    dragStartRef.current = null;
  }, [isCurrentlyDragging, onDragEnd]);

  // Set up global mouse event listeners during drag
  useEffect(() => {
    if (isCurrentlyDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isCurrentlyDragging, handleMouseMove, handleMouseUp]);

  // Set up global mouse event listeners during resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Set up global mouse event listeners during fade in
  useEffect(() => {
    if (isFadingIn) {
      document.addEventListener('mousemove', handleFadeInMove);
      document.addEventListener('mouseup', handleFadeInEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleFadeInMove);
        document.removeEventListener('mouseup', handleFadeInEnd);
      };
    }
  }, [isFadingIn, handleFadeInMove, handleFadeInEnd]);

  // Set up global mouse event listeners during fade out
  useEffect(() => {
    if (isFadingOut) {
      document.addEventListener('mousemove', handleFadeOutMove);
      document.addEventListener('mouseup', handleFadeOutEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleFadeOutMove);
        document.removeEventListener('mouseup', handleFadeOutEnd);
      };
    }
  }, [isFadingOut, handleFadeOutMove, handleFadeOutEnd]);

  const trackHeight = 80;
  
  
  const getTrackTypeColor = (type: string) => {
    switch (type) {
      case 'main': return '#4caf50';
      case 'music': return '#2196f3';
      case 'background': return '#ff9800';
      case 'sound': return '#9c27b0';
      case 'brainwave': return '#e91e63';
      case 'noise': return '#00bcd4';
      default: return '#666';
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const effectiveDuration = track.trimmedDuration || track.duration;

  return (
    <LaneContainer 
      $trackColor={track.color || getTrackTypeColor(track.type)}
      $isDragging={isCurrentlyDragging}
      className={className}
    >
      <TrackHeader>
        {track.type !== 'main' && onRemoveTrack && (
          <RemoveButton onClick={handleRemove} title="Remove track" data-no-drag>
            <Icon icon={FaTimes} />
          </RemoveButton>
        )}
        
        <TrackInfo>
          <TrackName>{track.name}</TrackName>
          <TrackType>
            {track.type}
            {track.isLoaded && (
              <DurationDisplay $isTrimmed={track.trimmedDuration !== undefined}>
                {track.type === 'brainwave' || track.type === 'noise' ? (
                  // For brainwave and noise tracks, just show the current duration (no original duration limit)
                  formatTime(effectiveDuration)
                ) : (
                  // For audio tracks, show trimmed/original format
                  <>
                    {formatTime(effectiveDuration)}
                    {track.trimmedDuration && ` / ${formatTime(track.duration)}`}
                  </>
                )}
              </DurationDisplay>
            )}
          </TrackType>
        </TrackInfo>
        
        <TrackControls data-no-drag>
          <ControlButton 
            $active={track.muted}
            onClick={handleMuteToggle}
          >
            <Icon icon={track.muted ? FaVolumeMute : FaVolumeUp} />
            {track.muted ? 'M' : ''}
          </ControlButton>
          
          <VolumeControl>
            <VolumeSlider
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={track.volume}
              onChange={handleVolumeChange}
            />
            <VolumeLabel>{Math.round(track.volume * 100)}%</VolumeLabel>
          </VolumeControl>
        </TrackControls>
      </TrackHeader>

      <TrackContent 
        $isDragging={isCurrentlyDragging}
      >
        {(track.type === 'brainwave' && track.brainwaveSettings) || (track.type === 'noise' && track.noiseSettings) ? (
          <WaveformContainer
            $startTime={track.startTime || 0}
            $pixelsPerSecond={pixelsPerSecond}
            $duration={effectiveDuration}
            $isDragging={isCurrentlyDragging}
            $color={track.color || getTrackTypeColor(track.type)}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
          >
            {track.type === 'brainwave' && track.brainwaveSettings ? (
              <BrainwaveTrackLane
                trackId={track.id}
                settings={track.brainwaveSettings}
                duration={track.trimmedDuration || track.duration}
                pixelsPerSecond={pixelsPerSecond}
                startTime={track.startTime || 0}
                visibleStartTime={visibleStartTime}
                visibleEndTime={visibleEndTime}
                onUpdateSettings={handleBrainwaveSettingsUpdate}
                onUpdateTrack={onUpdateTrack}
                isDragging={isCurrentlyDragging}
              />
            ) : (
              // For noise tracks, use a simple colored bar for now
              <div style={{
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${track.color}22 0%, ${track.color}44 50%, ${track.color}22 100%)`,
                borderRadius: '4px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: track.color,
                  fontSize: '12px',
                  fontWeight: 500,
                  opacity: 0.8
                }}>
                  {track.name}
                </div>
              </div>
            )}
            
            {/* Fade overlays for noise tracks */}
            {track.type === 'noise' && (
              <>
                {fadeInDuration > 0 && (
                  <FadeOverlay 
                    $type="in" 
                    $width={fadeInDuration * pixelsPerSecond} 
                  />
                )}
                {fadeOutDuration > 0 && (
                  <FadeOverlay 
                    $type="out" 
                    $width={fadeOutDuration * pixelsPerSecond} 
                  />
                )}
                
                {/* Fade handles */}
                <FadeHandle
                  $isActive={isFadingIn}
                  $position="left"
                  onMouseDown={handleFadeInStart}
                  data-no-drag
                />
                <FadeHandle
                  $isActive={isFadingOut}
                  $position="right"
                  onMouseDown={handleFadeOutStart}
                  data-no-drag
                />
                
                {/* Fade duration labels */}
                {isFadingIn && fadeInDuration > 0 && (
                  <FadeDurationLabel style={{ left: '4px' }}>
                    {fadeInDuration.toFixed(1)}s fade
                  </FadeDurationLabel>
                )}
                {isFadingOut && fadeOutDuration > 0 && (
                  <FadeDurationLabel style={{ right: '4px' }}>
                    {fadeOutDuration.toFixed(1)}s fade
                  </FadeDurationLabel>
                )}
              </>
            )}
            
            {/* Resize handle */}
            <ResizeHandle 
              $isResizing={isResizing}
              onMouseDown={handleResizeStart}
              data-no-drag
            />
          </WaveformContainer>
        ) : track.audioBuffer && track.isLoaded ? (
          <WaveformContainer
            $startTime={track.startTime || 0}
            $pixelsPerSecond={pixelsPerSecond}
            $duration={effectiveDuration}
            $isDragging={isCurrentlyDragging}
            $color={track.color || getTrackTypeColor(track.type)}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
          >
            <SynchronizedWaveformCanvas
              startTime={track.startTime || 0}
              width={effectiveDuration * pixelsPerSecond}
              height={trackHeight}
              audioBuffer={track.audioBuffer}
              pixelsPerSecond={pixelsPerSecond}
              visibleStartTime={visibleStartTime}
              visibleEndTime={visibleEndTime}
              color={track.color || getTrackTypeColor(track.type)}
            />
            

            
            {/* Fade overlays */}
            {fadeInDuration > 0 && (
              <FadeOverlay 
                $type="in" 
                $width={fadeInDuration * pixelsPerSecond} 
              />
            )}
            {fadeOutDuration > 0 && (
              <FadeOverlay 
                $type="out" 
                $width={fadeOutDuration * pixelsPerSecond} 
              />
            )}
            
            {/* Fade handles */}
            <FadeHandle
              $isActive={isFadingIn}
              $position="left"
              onMouseDown={handleFadeInStart}
              data-no-drag
            />
            <FadeHandle
              $isActive={isFadingOut}
              $position="right"
              onMouseDown={handleFadeOutStart}
              data-no-drag
            />
            
            {/* Fade duration labels */}
            {isFadingIn && fadeInDuration > 0 && (
              <FadeDurationLabel style={{ left: '4px' }}>
                {fadeInDuration.toFixed(1)}s fade
              </FadeDurationLabel>
            )}
            {isFadingOut && fadeOutDuration > 0 && (
              <FadeDurationLabel style={{ right: '4px' }}>
                {fadeOutDuration.toFixed(1)}s fade
              </FadeDurationLabel>
            )}
            
            {/* Resize handle */}
            <ResizeHandle 
              $isResizing={isResizing}
              onMouseDown={handleResizeStart}
              data-no-drag
            />
          </WaveformContainer>
        ) : (
          <EmptyTrackMessage>
            {track.isLoaded ? 'No audio data' : 'Loading...'}
          </EmptyTrackMessage>
        )}
      </TrackContent>
      
      {track.type !== 'main' && track.type !== 'brainwave' && contextMenu && (
        <WaveformContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          trackName={track.name}
          onDuplicate={handleDuplicate}
          onLoop={handleLoop}
          onRemove={handleRemove}
          onClose={handleCloseContextMenu}
        />
      )}
    </LaneContainer>
  );
};

export default TrackLane;